import { API_BASE_URL } from '../config/api'
import { tokenService } from './tokenService'

export interface TwoFactorStatus {
  enabled: boolean
  setupDate?: string
  lastUsed?: string
  backupCodesRemaining: number
  trustedDevices: number
  recommendations?: SecurityRecommendation[]
  securityScore?: number
}

export interface SecurityRecommendation {
  type: string
  priority: 'high' | 'medium' | 'low'
  title: string
  description: string
  action: string
}

export interface TwoFactorSetupInitiate {
  qrCodeUrl: string
  backupCodes: string[]
  setupKey: string
  instructions: {
    step1: string
    step2: string
    step3: string
  }
}

export interface TwoFactorSetupComplete {
  success: boolean
  message: string
  recommendation?: string
}

export interface TwoFactorVerify {
  success: boolean
  method?: 'totp' | 'backup'
  message: string
  backupCodesRemaining?: number
  error?: string
}

export interface TwoFactorDisable {
  success: boolean
  message: string
  warning?: string
  error?: string
}

export interface TwoFactorRegenerateBackupCodes {
  success: boolean
  backupCodes: string[]
  message: string
  warning?: string
  error?: string
}

export interface DeviceInfo {
  userAgent?: string
  screenResolution?: string
  timezone?: string
  language?: string
}

class TwoFactorAuthService {
  private baseUrl = `${API_BASE_URL}/api/v1/auth`

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await tokenService.getToken()
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }

  /**
   * Get current 2FA status and recommendations
   */
  async getStatus(): Promise<TwoFactorStatus> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/2fa/status`, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to get 2FA status' }))
        throw new Error(errorData.detail || 'Failed to get 2FA status')
      }

      const data = await response.json()
      return {
        enabled: data.enabled || false,
        setupDate: data.setup_date || undefined,
        lastUsed: data.last_used || undefined,
        backupCodesRemaining: data.backup_codes_remaining || 0,
        trustedDevices: data.trusted_devices || 0,
        recommendations: data.recommendations || [],
        securityScore: data.security_score || 0,
      }
    } catch (error) {
      console.error('TwoFactorAuthService: Failed to get status', error)
      throw error instanceof Error ? error : new Error('Failed to get 2FA status')
    }
  }

  /**
   * Initiate 2FA setup process
   */
  async initiateSetup(): Promise<TwoFactorSetupInitiate> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/2fa/setup/initiate`, {
        method: 'POST',
        headers,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to initiate 2FA setup' }))
        throw new Error(errorData.detail || 'Failed to initiate 2FA setup')
      }

      const data = await response.json()
      return {
        qrCodeUrl: data.qr_code_url,
        backupCodes: data.backup_codes || [],
        setupKey: data.setup_key,
        instructions: data.instructions || {
          step1: 'Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)',
          step2: 'Enter the 6-digit code from your app to complete setup',
          step3: 'Save the backup codes in a secure location',
        },
      }
    } catch (error) {
      console.error('TwoFactorAuthService: Failed to initiate setup', error)
      throw error instanceof Error ? error : new Error('Failed to initiate 2FA setup')
    }
  }

  /**
   * Complete 2FA setup by verifying the first code
   */
  async completeSetup(setupKey: string, verificationCode: string): Promise<TwoFactorSetupComplete> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/2fa/setup/complete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          setup_key: setupKey,
          verification_code: verificationCode,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to complete 2FA setup' }))
        throw new Error(errorData.detail || 'Failed to complete 2FA setup')
      }

      const data = await response.json()
      return {
        success: data.success || false,
        message: data.message || '2FA setup completed successfully',
        recommendation: data.recommendation,
      }
    } catch (error) {
      console.error('TwoFactorAuthService: Failed to complete setup', error)
      throw error instanceof Error ? error : new Error('Failed to complete 2FA setup')
    }
  }

  /**
   * Verify a 2FA code (TOTP or backup code)
   */
  async verifyCode(
    code: string,
    trustDevice: boolean = false,
    deviceInfo?: DeviceInfo
  ): Promise<TwoFactorVerify> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/2fa/verify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          code,
          trust_device: trustDevice,
          device_info: deviceInfo,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Invalid verification code' }))
        return {
          success: false,
          error: errorData.detail || 'Invalid verification code',
          message: errorData.detail || 'Invalid verification code',
        }
      }

      const data = await response.json()
      return {
        success: data.success || false,
        method: data.method,
        message: data.message || 'Verification successful',
        backupCodesRemaining: data.backup_codes_remaining,
      }
    } catch (error) {
      console.error('TwoFactorAuthService: Failed to verify code', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify code',
        message: error instanceof Error ? error.message : 'Failed to verify code',
      }
    }
  }

  /**
   * Disable 2FA for the current user
   */
  async disable(verificationCode: string): Promise<TwoFactorDisable> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/2fa/disable`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          verification_code: verificationCode,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to disable 2FA' }))
        throw new Error(errorData.detail || 'Failed to disable 2FA')
      }

      const data = await response.json()
      return {
        success: data.success || false,
        message: data.message || '2FA disabled successfully',
        warning: data.warning,
      }
    } catch (error) {
      console.error('TwoFactorAuthService: Failed to disable 2FA', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to disable 2FA',
        message: error instanceof Error ? error.message : 'Failed to disable 2FA',
      }
    }
  }

  /**
   * Regenerate backup codes
   */
  async regenerateBackupCodes(verificationCode: string): Promise<TwoFactorRegenerateBackupCodes> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.baseUrl}/2fa/regenerate-backup-codes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          verification_code: verificationCode,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to regenerate backup codes' }))
        throw new Error(errorData.detail || 'Failed to regenerate backup codes')
      }

      const data = await response.json()
      return {
        success: data.success || false,
        backupCodes: data.backup_codes || [],
        message: data.message || 'Backup codes regenerated successfully',
        warning: data.warning,
      }
    } catch (error) {
      console.error('TwoFactorAuthService: Failed to regenerate backup codes', error)
      return {
        success: false,
        backupCodes: [],
        error: error instanceof Error ? error.message : 'Failed to regenerate backup codes',
        message: error instanceof Error ? error.message : 'Failed to regenerate backup codes',
      }
    }
  }

  /**
   * Get device information for device trust
   */
  getDeviceInfo(): DeviceInfo {
    return {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      screenResolution:
        typeof window !== 'undefined'
          ? `${window.screen.width}x${window.screen.height}`
          : undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: typeof navigator !== 'undefined' ? navigator.language : undefined,
    }
  }
}

export const twoFactorAuthService = new TwoFactorAuthService()






